export type ResolveAxisMaxOptions = {
  /** When `max` is `available`, reserve sibling space by current size or CSS min-size. */
  reserveSiblingSize?: 'current' | 'min';
};

export const roundPx = (value: number) => Math.round(value);

export const clampSize = (value: number, min: number, max?: number): number => {
  const next = Math.max(min, roundPx(value));

  if (max === undefined) {
    return next;
  }

  return Math.max(min, Math.min(max, next));
};

const parseFlexGapPx = (
  parent: HTMLElement,
  axis: 'width' | 'height',
): number => {
  const style = getComputedStyle(parent);
  const gapValue =
    axis === 'width' ? style.columnGap || style.gap : style.rowGap || style.gap;

  if (!gapValue || gapValue === 'normal') {
    return 0;
  }

  const parsed = Number.parseFloat(gapValue);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const readMinSizePx = (
  element: HTMLElement,
  axis: 'width' | 'height',
): number => {
  const style = getComputedStyle(element);
  const raw = axis === 'width' ? style.minWidth : style.minHeight;

  if (!raw || raw === 'auto' || raw === 'none') {
    return 0;
  }

  const parsed = Number.parseFloat(raw);
  return Number.isNaN(parsed) ? 0 : roundPx(parsed);
};

export const resolveAxisMaxPx = (
  element: HTMLElement | null,
  axis: 'width' | 'height',
  max: number | 'available' | undefined,
  options?: ResolveAxisMaxOptions,
): number | undefined => {
  if (max === undefined) {
    return undefined;
  }

  if (typeof max === 'number') {
    return max;
  }

  const parent = element?.parentElement;

  if (!parent) {
    return undefined;
  }

  const parentSize =
    axis === 'width' ? parent.clientWidth : parent.clientHeight;
  const children = Array.from(parent.children) as HTMLElement[];
  const gap = parseFlexGapPx(parent, axis);
  const totalGap = children.length > 1 ? gap * (children.length - 1) : 0;
  const reserveSiblingSize = options?.reserveSiblingSize ?? 'current';

  let siblingsSize = 0;

  for (const child of children) {
    if (child === element) {
      continue;
    }

    let siblingSize = 0;
    if (reserveSiblingSize === 'min') {
      siblingSize = readMinSizePx(child, axis);
    } else if (axis === 'width') {
      siblingSize = child.offsetWidth;
    } else {
      siblingSize = child.offsetHeight;
    }

    siblingsSize += siblingSize;
  }

  return roundPx(Math.max(0, parentSize - siblingsSize - totalGap));
};
